import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Button, confirm, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  DownloadIcon,
  EditIcon,
  HelpCircle,
  TrashIcon,
} from "lucide-react";
import { useAuditRecordQuery, useDeleteAuditRecord } from "../api/queries";
import {
  AuditRecordDetailTour,
  useAuditRecordDetailTour,
} from "../tour/AuditRecordDetailTour";
import { AuditRecordDetailContent } from "../ui/AuditRecordDetailContent";

export function AuditRecordDetailPage() {
  const { id } = useParams({ from: "/app/internal-audit/records/$id" });
  const nav = useNavigate({ from: "/app/internal-audit/records/$id" });
  const tour = useAuditRecordDetailTour();

  const { data: record, isLoading } = useAuditRecordQuery(id);
  const deleteMutation = useDeleteAuditRecord();

  // Permission check for managing issues
  const canManageIssues = useActionPermission(["internal_audit:create"]);

  const handleBack = () => {
    nav({
      to: "/app/internal-audit/records",
      search: {
        offset: 0,
        limit: 20,
        sort: undefined,
        filters: undefined,
      },
    });
  };

  const handleEdit = () => {
    if (record?.status === "completed") {
      toast.error("ບໍ່ສາມາດແກ້ໄຂບັນທຶກທີ່ສຳເລັດແລ້ວໄດ້");
      return;
    }
    nav({ to: "/app/internal-audit/records/$id/edit", params: { id } });
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: "ຢືນຢັນການລຶບ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບບັນທຶກການກວດສອບນີ້? ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.",
      actionText: "ລຶບ",
      cancelText: "ຍົກເລີກ",
      ActionProps: {
        variant: "destructive",
      },
    });

    if (!ok) return;

    try {
      await deleteMutation.run(id);
      nav({
        to: "/app/internal-audit/records",
        search: {
          offset: 0,
          limit: 20,
          sort: undefined,
          filters: undefined,
        },
      });
    } catch {
      // Error toast already handled by mutation
    }
  };

  const handleDownloadPDF = () => {
    if (record) {
      nav({
        to: "/app/internal-audit/records/$id/pdf-preview",
        params: { id: record.id },
      });
    }
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກໍາລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (!record) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] flex-col items-center justify-center">
            <p className="text-muted-foreground">ບໍ່ພົບບັນທຶກການກວດສອບ</p>
            <Button variant="outline" onClick={handleBack} className="mt-4">
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              ກັບໄປ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />

      <Main>
        <AuditRecordDetailTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={handleBack}>
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
            <div className="flex items-center space-x-2">
              <div>
                <h1 className="font-bold text-3xl">ລາຍລະອຽດບັນທຶກການກວດສອບ</h1>
                <p className="mt-1 text-muted-foreground">
                  ຂໍ້ມູນລາຍລະອຽດຂອງບັນທຶກການກວດສອບ
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleDownloadPDF}
              className="gap-2"
            >
              <DownloadIcon className="h-4 w-4" />
              ເບິ່ງ PDF Preview
            </Button>
            <Button
              variant="outline"
              onClick={handleEdit}
              disabled={record.status === "completed"}
            >
              <EditIcon className="mr-2 h-4 w-4" />
              ແກ້ໄຂ
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              isLoading={deleteMutation.isPending}
            >
              <TrashIcon className="mr-2 h-4 w-4" />
              ລຶບ
            </Button>
          </div>
        </div>

        <AuditRecordDetailContent
          record={record}
          canManageIssues={canManageIssues}
        />
      </Main>
    </>
  );
}
